import React from 'react';
import { Page, Document, StyleSheet, View, Text } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { 
    padding: 30, 
    fontSize: 10, 
    fontFamily: 'Helvetica',
    backgroundColor: '#fff'
  },
  header: { 
    marginBottom: 20, 
    textAlign: 'center', 
    borderBottom: 1, 
    borderBottomColor: '#ccc',
    paddingBottom: 10 
  },
  title: { 
    fontSize: 18, 
    fontWeight: 'bold',
    marginBottom: 4
  },
  subtitle: { 
    fontSize: 12, 
    color: '#555', 
    marginTop: 5 
  },
  section: { 
    marginBottom: 15 
  },
  sectionTitle: { 
    fontSize: 14, 
    fontWeight: 'bold', 
    marginBottom: 8, 
    backgroundColor: '#f0f0f0', 
    padding: 5,
    borderRadius: 4
  },
  row: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    marginBottom: 4,
    paddingHorizontal: 4
  },
  footer: { 
    position: 'absolute', 
    bottom: 20, 
    left: 30, 
    right: 30, 
    textAlign: 'center', 
    fontSize: 8, 
    color: '#888',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 8
  },
  categoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    borderBottom: '1 solid #eee',
    paddingBottom: 2
  },
  evolutionHeader: {
    flexDirection: 'row',
    borderBottom: 1,
    paddingBottom: 4,
    marginBottom: 6,
    fontWeight: 'bold',
    backgroundColor: '#fafafa'
  },
  evolutionRow: {
    flexDirection: 'row',
    marginBottom: 3
  },
  colMonth: { width: '30%' },
  colValue: { width: '23%', textAlign: 'right' },
  colLucro: { width: '24%', textAlign: 'right' }
});

const formatCurrency = (v) => 
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const RelatorioFaturamentoPDF = ({ 
  resumo, 
  evolucao, 
  despesasPorCategoria, 
  periodo, 
  empresa 
}) => {
  // Garantir que os objetos existem
  const safeResumo = resumo || {};
  const safeEvolucao = Array.isArray(evolucao) ? evolucao : [];
  const safeDespesas = Array.isArray(despesasPorCategoria) ? despesasPorCategoria : [];
  const nomeEmpresa = empresa || 'Minha Empresa';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <Text style={styles.title}>{nomeEmpresa}</Text>
          <Text style={styles.subtitle}>Relatório de Faturamento</Text>
          <Text>Período: {periodo.dataInicio || 'início'} até {periodo.dataFim || 'hoje'}</Text>
          <Text>Gerado em: {new Date().toLocaleString('pt-BR')}</Text>
        </View>

        {/* Resumo Financeiro */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Resumo Financeiro</Text>
          <View style={styles.row}>
            <Text>Receita Bruta:</Text>
            <Text>{formatCurrency(safeResumo.receita_total)}</Text>
          </View>
          <View style={styles.row}>
            <Text>(-) Custo dos Produtos (CMV):</Text>
            <Text>{formatCurrency(safeResumo.custo_produtos)}</Text>
          </View>
          <View style={styles.row}>
            <Text>(-) Despesas Operacionais:</Text>
            <Text>{formatCurrency(safeResumo.despesas_operacionais)}</Text>
          </View>
          <View style={[styles.row, { marginTop: 6, borderTop: '1 solid #ccc', paddingTop: 4 }]}>
            <Text style={{ fontWeight: 'bold' }}>Lucro Bruto:</Text>
            <Text style={{ fontWeight: 'bold' }}>{formatCurrency(safeResumo.lucro_bruto)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={{ fontWeight: 'bold' }}>Lucro Líquido:</Text>
            <Text style={{ fontWeight: 'bold' }}>{formatCurrency(safeResumo.lucro_liquido)}</Text>
          </View>
          <View style={styles.row}>
            <Text>Margem Bruta:</Text>
            <Text>{safeResumo.margem_bruta?.toFixed(1) || '0.0'}%</Text>
          </View>
          <View style={styles.row}>
            <Text>Margem Líquida:</Text>
            <Text>{safeResumo.margem_liquida?.toFixed(1) || '0.0'}%</Text>
          </View>
        </View>

        {/* Despesas por Categoria */}
        {safeDespesas.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Despesas por Categoria</Text>
            {safeDespesas.map((cat, idx) => (
              <View key={idx} style={styles.categoryItem}>
                <Text>{cat.categoria?.charAt(0).toUpperCase() + cat.categoria?.slice(1)}</Text>
                <Text>{formatCurrency(cat.total)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Evolução Mensal */}
        {safeEvolucao.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Evolução Mensal (últimos meses)</Text>
            <View style={styles.evolutionHeader}>
              <Text style={styles.colMonth}>Mês</Text>
              <Text style={styles.colValue}>Receita</Text>
              <Text style={styles.colValue}>Custo</Text>
              <Text style={styles.colLucro}>Lucro</Text>
            </View>
            {safeEvolucao.slice(-6).map((item, idx) => (
              <View key={idx} style={styles.evolutionRow}>
                <Text style={styles.colMonth}>{item.periodo}</Text>
                <Text style={styles.colValue}>{formatCurrency(item.receita)}</Text>
                <Text style={styles.colValue}>{formatCurrency(item.custo)}</Text>
                <Text style={styles.colLucro}>{formatCurrency(item.lucro)}</Text>
              </View>
            ))}
          </View>
        )}

        <Text style={styles.footer}>
          Relatório gerado automaticamente pelo sistema FluxoEasy
        </Text>
      </Page>
    </Document>
  );
};

export default RelatorioFaturamentoPDF;